import React from "react";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO";

const Datenschutz: React.FC = () => {
  const { t, i18n } = useTranslation();

  return (
    <section className="w-full flex justify-center px-4 py-12 bg-black text-white">
      <div className="w-full max-w-4xl">
        <SEO
          title={t("datenschutz.title")}
          description={t("datenschutz.description")}
          keywords={t("datenschutz.keywords")}
        />

        {/* Headline */}
        <h1 className="text-3xl font-extrabold underline underline-offset-8 decoration-brand-red-text mb-8 text-center">
          {t("datenschutz.title")}
        </h1>

        {/* Last Updated */}
        <p className="text-gray-400 text-center mb-8">
          {t("datenschutz.lastUpdated")}: 27. August 2025
        </p>

        {/* Introduction */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("datenschutz.introduction.title")}
          </h2>
          <p className="text-gray-300 leading-relaxed mb-4 text-center md:text-left">
            {t("datenschutz.introduction.text")}
          </p>
        </div>

        {/* Data Collection */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("datenschutz.dataCollection.title")}
          </h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-xl font-semibold mb-2 text-center">
                {t("datenschutz.dataCollection.personal.title")}
              </h3>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 md:ml-0 text-center md:text-left">
                <li>{t("datenschutz.dataCollection.personal.name")}</li>
                <li>{t("datenschutz.dataCollection.personal.email")}</li>
                <li>{t("datenschutz.dataCollection.personal.phone")}</li>
                <li>{t("datenschutz.dataCollection.personal.course")}</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2 text-center">
                {t("datenschutz.dataCollection.technical.title")}
              </h3>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 md:ml-0 text-center md:text-left">
                <li>{t("datenschutz.dataCollection.technical.ip")}</li>
                <li>{t("datenschutz.dataCollection.technical.browser")}</li>
                <li>{t("datenschutz.dataCollection.technical.cookies")}</li>
                <li>{t("datenschutz.dataCollection.technical.analytics")}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Purpose of Data Collection */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("datenschutz.purpose.title")}
          </h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 md:ml-0 text-center md:text-left">
            <li>{t("datenschutz.purpose.courseRegistration")}</li>
            <li>{t("datenschutz.purpose.communication")}</li>
            <li>{t("datenschutz.purpose.websiteImprovement")}</li>
            <li>{t("datenschutz.purpose.legalObligations")}</li>
          </ul>
        </div>

        {/* Third Party Services */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("datenschutz.thirdParty.title")}
          </h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-xl font-semibold mb-2 text-center">Cloudinary</h3>
              <p className="text-gray-300 text-center md:text-left">
                {t("datenschutz.thirdParty.cloudinary")}
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2 text-center">Stripe</h3>
              <p className="text-gray-300 text-center md:text-left">
                {t("datenschutz.thirdParty.stripe")}
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2 text-center">Google Analytics</h3>
              <p className="text-gray-300 text-center md:text-left">
                {t("datenschutz.thirdParty.analytics")}
              </p>
            </div>
          </div>
        </div>

        {/* Data Storage */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("datenschutz.storage.title")}
          </h2>
          <p className="text-gray-300 leading-relaxed mb-4 text-center md:text-left">
            {t("datenschutz.storage.text")}
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 md:ml-0 text-center md:text-left">
            <li>{t("datenschutz.storage.security")}</li>
            <li>{t("datenschutz.storage.access")}</li>
            <li>{t("datenschutz.storage.backup")}</li>
          </ul>

          {/* Data Retention */}
          <div className="mt-4">
            <h3 className="text-xl font-semibold mb-2 text-center">
              {t("datenschutz.storage.retention.title")}
            </h3>
            <p className="text-gray-300 leading-relaxed text-center md:text-left">
              {t("datenschutz.storage.retention.text")}
            </p>
          </div>
        </div>

        {/* User Rights */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("datenschutz.userRights.title")}
          </h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 md:ml-0 text-center md:text-left">
            <li>{t("datenschutz.userRights.access")}</li>
            <li>{t("datenschutz.userRights.correction")}</li>
            <li>{t("datenschutz.userRights.deletion")}</li>
            <li>{t("datenschutz.userRights.portability")}</li>
            <li>{t("datenschutz.userRights.objection")}</li>
          </ul>
        </div>

        {/* Data Breach Notification */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("datenschutz.dataBreach.title")}
          </h2>
          <p className="text-gray-300 leading-relaxed mb-4 text-center md:text-left">
            {t("datenschutz.dataBreach.text")}
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 md:ml-0 text-center md:text-left">
            <li>{t("datenschutz.dataBreach.notification")}</li>
            <li>{t("datenschutz.dataBreach.measures")}</li>
            <li>{t("datenschutz.dataBreach.authorities")}</li>
          </ul>
        </div>

        {/* Cookies */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("datenschutz.cookies.title")}
          </h2>
          <p className="text-gray-300 leading-relaxed mb-4 text-center md:text-left">
            {t("datenschutz.cookies.text")}
          </p>
          <div className="space-y-4">
            <div>
              <h3 className="text-xl font-semibold mb-2 text-center">
                {t("datenschutz.cookies.essential.title")}
              </h3>
              <p className="text-gray-300 text-center md:text-left">
                {t("datenschutz.cookies.essential.text")}
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2 text-center">
                {t("datenschutz.cookies.analytics.title")}
              </h3>
              <p className="text-gray-300 text-center md:text-left">
                {t("datenschutz.cookies.analytics.text")}
              </p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("datenschutz.contact.title")}
          </h2>
          <p className="text-gray-300 leading-relaxed mb-4 text-center md:text-left">
            {t("datenschutz.contact.text")}
          </p>
          <div className="bg-gray-800 p-6 rounded-lg text-center md:text-left">
            <p className="text-white font-semibold mb-2">
              Internationale in Kunst e.V.
            </p>
            <p className="text-gray-300">Email: {t("datenschutz.contact.email")}</p>
            <p className="text-gray-300">
              {t("datenschutz.contact.address")}
            </p>
          </div>
        </div>

        {/* Changes to Policy */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("datenschutz.changes.title")}
          </h2>
          <p className="text-gray-300 leading-relaxed text-center md:text-left">
            {t("datenschutz.changes.text")}
          </p>
        </div>

        {/* Terms of Service */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-brand-red-text text-center">
            {t("datenschutz.terms.title")}
          </h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-xl font-semibold mb-2 text-center">
                {t("datenschutz.terms.acceptance.title")}
              </h3>
              <p className="text-gray-300 leading-relaxed text-center md:text-left">
                {t("datenschutz.terms.acceptance.text")}
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2 text-center">
                {t("datenschutz.terms.services.title")}
              </h3>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 md:ml-0 text-center md:text-left">
                <li>{t("datenschutz.terms.services.courses")}</li>
                <li>{t("datenschutz.terms.services.events")}</li>
                <li>{t("datenschutz.terms.services.communication")}</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2 text-center">
                {t("datenschutz.terms.liability.title")}
              </h3>
              <p className="text-gray-300 leading-relaxed text-center md:text-left">
                {t("datenschutz.terms.liability.text")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Datenschutz;
